/**
 * AdminFunnel — conversion-funnel widget.
 *
 * Reads /api/admin/analytics/tier-gate-summary and renders:
 *  • total intent events + unique users in the window
 *  • horizontal bar chart of clicks per surface
 *  • required-tier split + table of detailed rows
 *
 * Use case: "Which gated feature is creating the most upgrade intent?"
 */
import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, RefreshCw, Crown, Users } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SURFACE_LABELS = {
  subscription_button:  'Navbar Pill',
  competition_locked:   'Locked Competition',
  lake_pro_card:        'Lake Pro Intel',
  recommendations_lock: 'Recommendations Lock',
};

const fmtSurface = (s) => SURFACE_LABELS[s] || s.replace(/_/g, ' ');

const AdminFunnel = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/analytics/tier-gate-summary?days=${days}`, { headers });
      setData(d);
    } catch {
      toast.error('Failed to load funnel summary');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const maxCount = useMemo(
    () => Math.max(1, ...(data?.by_surface || []).map((r) => r.count)),
    [data],
  );

  return (
    <section
      className="mt-12 rounded-2xl border border-white/10 bg-[#0b0d0b] p-6 md:p-8"
      data-testid="admin-funnel"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="data-label text-[#c8b273] mb-1 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" />
            Conversion funnel
          </p>
          <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white">
            Where intent comes from
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px] bg-[#141714] border-white/10 text-white" data-testid="funnel-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0b0d0b] border-white/10">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-[#a8b0a8] hover:text-white">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <Stat label="Intent events" value={data?.totals?.count ?? 0} testId="funnel-total" />
        <Stat label="Unique users"   value={data?.totals?.unique_users ?? 0} icon={Users} testId="funnel-users" />
        <Stat label="Window"         value={`${days}d`} icon={Crown} testId="funnel-window-display" />
      </div>

      {loading ? (
        <p className="text-[#8a948a] text-sm">Loading funnel data…</p>
      ) : (data?.by_surface || []).length === 0 ? (
        <p className="text-[#8a948a] text-sm" data-testid="funnel-empty">
          No tier-gate events recorded in the selected window. As anglers click the
          Crown / Upgrade CTAs around the platform, you&apos;ll see them appear here.
        </p>
      ) : (
        <div className="space-y-3" data-testid="funnel-bars">
          {data.by_surface.map((row, i) => {
            const pct = Math.round((row.count / maxCount) * 100);
            const tierBadge =
              row.required_tier === 'elite'
                ? 'bg-[#c8b273] text-black'
                : row.required_tier === 'pro'
                ? 'bg-[#c8b273]/20 text-[#c8b273] border border-[#c8b273]/40'
                : 'bg-white/5 text-[#a8b0a8] border border-white/10';
            return (
              <motion.div
                key={`${row.surface}-${row.required_tier || 'na'}-${i}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: i * 0.04 }}
                className="rounded-lg border border-white/10 bg-[#141714] p-4"
                data-testid={`funnel-bar-${row.surface}`}
              >
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <p className="font-bold text-white text-sm">{fmtSurface(row.surface)}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded uppercase tracking-widest text-[10px] font-bold ${tierBadge}`}>
                      {row.required_tier || 'any'}
                    </span>
                    <span className="text-[#c8b273] font-bold tabular-nums">{row.count}</span>
                    <span className="text-[#6b7d6b]">· {row.unique_users} users</span>
                  </div>
                </div>
                <div className="h-2 rounded bg-black/40 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.04 }}
                    className="h-full bg-gradient-to-r from-[#c8b273] to-[#d8c283]"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const Stat = ({ label, value, icon: Icon, testId }) => (
  <div className="rounded-lg border border-white/10 bg-[#141714] p-3" data-testid={testId}>
    <p className="data-label text-[#a8b0a8] text-[10px] mb-1 flex items-center gap-1">
      {Icon ? <Icon className="w-3 h-3" /> : null}
      {label}
    </p>
    <p className="font-['Barlow_Condensed'] text-3xl font-bold text-white">{value}</p>
  </div>
);

export default AdminFunnel;
